import { RoleType, PacketStatus } from '@promo/shared';

export function canViewAssessmentsTabs({
  userId,
  globalRoles,
  roleAssignments
}: {
  userId: string,
  globalRoles: string[], // e.g. [RoleType.SENIOR_LEADER, RoleType.HR]
  roleAssignments: Partial<Record<RoleType, string[]>>
}): boolean {
  const packetRoles: RoleType[] = [
    RoleType.MANAGER,
    RoleType.ASSESSOR,
    RoleType.BAR_RAISER,
    RoleType.SENIOR_LEADER,
    RoleType.HR,
  ];
  for (const role of packetRoles) {
    if (roleAssignments[role]?.includes(userId)) return true;
  }
  if (globalRoles.includes(RoleType.SENIOR_LEADER) || globalRoles.includes(RoleType.HR)) return true;
  return false;
}

export function hasPrivilegedRole({
  userId,
  globalRoles,
  packetRoles
}: {
  userId: string;
  globalRoles: string[];
  packetRoles: Partial<Record<RoleType, string[]>>;
}) {
  const privilegedRoles: RoleType[] = [
    RoleType.MANAGER,
    RoleType.SENIOR_LEADER,
    RoleType.HR,
    RoleType.BAR_RAISER,
  ];
  // Check global
  if (globalRoles.some(r => privilegedRoles.includes(r as RoleType))) return true;
  // Check packet-specific (across all packets, but fallback to current packet if not available)
  for (const role of privilegedRoles) {
    if (packetRoles[role] && packetRoles[role]!.includes(userId)) return true;
  }
  return false;
}

export function canViewCompletedPacketDetails({
  userId,
  candidateId,
  assessorIds,
  globalRoles,
  packetRoles
}: {
  userId: string;
  candidateId: string;
  assessorIds: string[];
  globalRoles: string[];
  packetRoles: Partial<Record<RoleType, string[]>>;
}) {
  if (!userId) return false;
  if (userId === candidateId) return true;
  if (assessorIds.includes(userId)) return true;
  const privilegedRoles: RoleType[] = [RoleType.MANAGER, RoleType.SENIOR_LEADER, RoleType.HR, RoleType.BAR_RAISER];
  if (globalRoles.some(r => privilegedRoles.includes(r as RoleType))) return true;
  for (const role of privilegedRoles) {
    if (packetRoles[role]?.includes(userId)) return true;
  }
  return false;
}

export function determineShowAssessmentTabs({
  packet,
  userId,
  globalRoles,
  roleAssignments,
  isAssessor
}: {
  packet: { status: PacketStatus } | null;
  userId: string;
  globalRoles: string[];
  roleAssignments: Partial<Record<RoleType, string[]>>;
  isAssessor: boolean;
}): boolean {
  const hasPrivileged = hasPrivilegedRole({
    userId,
    globalRoles,
    packetRoles: roleAssignments,
  });

  let showAssessmentTabs = false;
  if (packet?.status === PacketStatus.COMPLETED) {
    // Candidates and endorsers should NOT see assessment tabs even when completed
    showAssessmentTabs = isAssessor || hasPrivileged;
  } else {
    // For packets still in progress, allow assessors to view tabs even if roleAssignments query hasn't resolved yet
    showAssessmentTabs = isAssessor || canViewAssessmentsTabs({
      userId,
      globalRoles,
      roleAssignments,
    });
  }

  return showAssessmentTabs;
} 